/*
 * Church search — unified single-field search
 *
 * Vanilla JS (no jQuery dependency)
 * - fetch + AbortController (was: $.ajax)
 * - querySelector / classList (was: $() / addClass / removeClass)
 * - Single shared InfoWindow
 * - O(1) active marker tracking
 * - Batched marker creation via requestAnimationFrame
 * - Sidebar built with documentFragment
 * - search_all skips sidebar/listing build
 */
import { getUrlParameter, isValidPostcode, sendEvent } from "./global.js";

if (!document.body.classList.contains("find-a-church")) {
	// Not the church finder page — bail out early
} else {
	initMap();
}

// --- Helpers ---

function qs(selector, root = document) {
	return root.querySelector(selector);
}

function qsa(selector, root = document) {
	return Array.from(root.querySelectorAll(selector));
}

function scrollToEl(el) {
	if (!el) return;
	window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY, behavior: "smooth" });
}

function isValidHttpUrl(string) {
	try {
		const url = new URL(string);
		return url.protocol === "http:" || url.protocol === "https:";
	} catch (_) {
		return false;
	}
}

// --- State ---

let map;
let abortController = null;
let markerCluster;
let infowindow;
let activeMarker = null;

const global_markers = [];
const global_markers_data = [];
let newlistings = [];
let search_url = "";
let overviewCenter;
let overviewZoom;

// View (map | list) + list-rendering state
let currentView = "map";
let currentListType = "all"; // "all" | "search"
let currentListings = []; // dataset currently backing the list
let filteredListings = []; // currentListings after the client-side filter
let allChurchesData = null; // cached search-all payload
let listPage = 0; // pages of the list rendered so far
let currentLetter = "all"; // selected A–Z letter ("all" = no letter filter)
let listObserver = null;
let listSentinel = null;
const LIST_PAGE_SIZE = 40;

const PIN_SVG =
	"M12,11.5A2.5,2.5 0 0,1 9.5,9A2.5,2.5 0 0,1 12,6.5A2.5,2.5 0 0,1 14.5,9A2.5,2.5 0 0,1 12,11.5M12,2A7,7 0 0,0 5,9C5,14.25 12,22 12,22C12,22 19,14.25 19,9A7,7 0 0,0 12,2Z";

let search_icon;
let search_icon_hover;

const isDevMode = document.body.classList.contains("devmode-true");

// Cluster SVG (computed once)
const CLUSTER_SVG = window.btoa(`
	<svg fill="#2bb2bc" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240">
	<circle cx="120" cy="120" opacity="1" r="70" />
	<circle cx="120" cy="120" opacity=".7" r="90" />
	<circle cx="120" cy="120" opacity=".3" r="110" />
	<circle cx="120" cy="120" opacity=".2" r="130" />
	</svg>`);

const CLUSTER_RENDERER = {
	render: ({ count, position }) =>
		new google.maps.Marker({
			label: {
				text: String(count),
				color: "#fff",
				fontFamily: "proxima-nova, sans-serif",
				fontSize: "13px",
				fontWeight: "600",
			},
			icon: {
				url: `data:image/svg+xml;base64,${CLUSTER_SVG}`,
				scaledSize: new google.maps.Size(45, 45),
			},
			position,
			zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
		}),
};

// --- Cached DOM references ---

let searchInput;
let mapContainer;
let noResults;
let sidebar;
let resultsContainer;
let sidebarTemplate;
let finderWrapper;
let viewToggleBtns;
let azButtons;
let listCount;
let listLoading;
let listEmpty;

// --- Init ---

async function initMap() {
	const { Map } = await google.maps.importLibrary("maps");
	const { Autocomplete } = await google.maps.importLibrary("places");

	// Cache DOM references
	searchInput = document.getElementById("church_search");
	mapContainer = qs(".church-finder-map-container");
	noResults = qs(".no-results");
	sidebar = qs(".search-sidebar");
	resultsContainer = qs(".results");
	sidebarTemplate = qs(".sidebar-template");
	finderWrapper = qs(".church-finder-wrapper");
	viewToggleBtns = qsa(".view-toggle-btn");
	azButtons = qsa(".az-btn");
	listCount = qs(".list-count");
	listLoading = qs(".list-loading");
	listEmpty = qs(".list-empty");

	// Initial view comes from the wrapper (set by the no-flash inline script in the template)
	currentView = finderWrapper && finderWrapper.getAttribute("data-view") === "list" ? "list" : "map";
	viewToggleBtns.forEach((btn) => {
		btn.setAttribute("aria-pressed", btn.dataset.view === currentView ? "true" : "false");
	});

	search_icon = {
		path: PIN_SVG,
		anchor: new google.maps.Point(12, 17),
		fillOpacity: 1,
		fillColor: "#2bb2bc",
		strokeWeight: 1,
		strokeColor: "#1c9da0",
		scale: 2,
	};
	search_icon_hover = {
		path: PIN_SVG,
		anchor: new google.maps.Point(12, 17),
		fillOpacity: 1,
		fillColor: "#3c4981",
		strokeWeight: 1,
		strokeColor: "#2e3d6b",
		scale: 2,
	};

	// Read URL params
	const url_lat = parseFloat(getUrlParameter("search_lat"));
	const url_lon = parseFloat(getUrlParameter("search_lon"));
	const url_place = decodeURIComponent(getUrlParameter("search_place") || "");
	const url_name = decodeURIComponent(getUrlParameter("search_name") || "");
	const url_search = decodeURIComponent(getUrlParameter("search") || "");

	const map_center = {
		lat: url_lat || 53.76293458756777,
		lng: url_lon || -2.1087462927010394,
	};
	const map_zoom = 6;

	map = new Map(document.getElementById("map"), {
		center: map_center,
		zoom: map_zoom,
		mapId: "10c54dda61bbc61b",
		mapTypeControl: false,
	});

	overviewCenter = map_center;
	overviewZoom = map_zoom;

	// Single shared InfoWindow
	infowindow = new google.maps.InfoWindow({ maxWidth: 320 });
	infowindow.addListener("closeclick", () => restoreOverview());

	// Google Places Autocomplete
	const autocomplete = new Autocomplete(searchInput);
	autocomplete.setComponentRestrictions({ country: ["gb"] });

	autocomplete.addListener("place_changed", () => {
		const place = autocomplete.getPlace();
		if (place.geometry) {
			const lat = place.geometry.location.lat();
			const lng = place.geometry.location.lng();
			search_lat_lon(lat, lng);
			map.setCenter(place.geometry.location);
		}
	});

	// Determine initial search from URL
	if (url_lat && url_lon) {
		search_lat_lon(url_lat, url_lon);
		reverseGeocode(url_lat, url_lon);
	} else if (url_place && url_place !== "undefined") {
		search_place(url_place);
		searchInput.value = url_place;
	} else if (url_name && url_name !== "undefined") {
		search_name(url_name);
		searchInput.value = url_name;
	} else if (url_search && url_search !== "undefined") {
		searchInput.value = url_search;
		dispatchSearch(url_search);
	} else {
		search_all();
	}

	// --- Event listeners ---

	const searchForm = document.getElementById("church-search-form");
	if (searchForm) {
		searchForm.addEventListener("submit", (e) => {
			e.preventDefault();
			const query = searchInput.value.trim();
			cancelPendingRequest();
			if (query.length === 0) {
				search_all();
			} else if (query.length >= 2) {
				dispatchSearch(query);
			}
		});
	}

	// View all link
	qsa(".view-all-churches").forEach((el) => {
		el.addEventListener("click", (e) => {
			e.preventDefault();
			search_all();
			scrollToMap();
		});
	});

	// Map / List view toggle
	viewToggleBtns.forEach((btn) => {
		btn.addEventListener("click", () => setView(btn.dataset.view));
	});

	// List view A–Z filter
	azButtons.forEach((btn) => {
		btn.addEventListener("click", () => {
			if (btn.disabled) return;
			setLetter(btn.dataset.letter);
		});
	});

	// Sidebar click — delegated from .results container
	if (resultsContainer) {
		resultsContainer.addEventListener("click", (e) => {
			const church = e.target.closest(".church");
			if (!church) return;

			// In list view the map is hidden — just expand/collapse the row inline
			if (currentView === "list") {
				toggleChurchDetails(church);
				return;
			}

			// Map view: clicking an already-expanded church collapses it
			if (church.classList.contains("expanded")) {
				restoreOverview();
			} else {
				const uid = church.dataset.churchUid;
				const marker_id = church.dataset.markerId;
				const markerData = global_markers_data[marker_id];
				if (markerData && markerData.marker) {
					expandChurch(uid, markerData.marker, false);
				}
			}
		});
	}

	// Geolocation
	if (navigator.geolocation) {
		document.addEventListener("click", (e) => {
			if (!e.target.closest(".get-location")) return;
			navigator.geolocation.getCurrentPosition(
				(position) => {
					const marker = new google.maps.Marker({
						position: new google.maps.LatLng(position.coords.latitude, position.coords.longitude),
						draggable: false,
						icon: {
							path: google.maps.SymbolPath.CIRCLE,
							fillColor: "#459bea",
							fillOpacity: 1,
							scale: 11,
							strokeWeight: 0,
						},
					});
					marker.setMap(map);
					search_lat_lon(position.coords.latitude, position.coords.longitude);
					reverseGeocode(position.coords.latitude, position.coords.longitude);
				},
				() => {
					alert("There was a problem finding your location");
				}
			);
		});
	}
}

// --- Fetch helpers ---

function cancelPendingRequest() {
	if (abortController) {
		abortController.abort();
		abortController = null;
	}
}

async function fetchJson(url, params = {}) {
	cancelPendingRequest();
	abortController = new AbortController();

	const query = new URLSearchParams(params).toString();
	const fullUrl = query ? `${url}?${query}` : url;

	const response = await fetch(fullUrl, {
		method: "GET",
		headers: { Accept: "application/json" },
		signal: abortController.signal,
	});

	if (!response.ok) {
		throw new Error(`HTTP ${response.status}`);
	}

	return response.json();
}

// --- Search loader ---

function searchLoader(option) {
	if (mapContainer) {
		mapContainer.classList.toggle("searching", option === "start");
	}
	if (currentView === "list") {
		showListLoading(option === "start");
		if (option === "start") {
			if (listEmpty) listEmpty.classList.add("hidden");
			if (noResults) noResults.classList.add("hidden");
		}
	}
}

// --- Search functions ---

function dispatchSearch(query) {
	if (!query || query.length < 2) {
		search_all();
		return;
	}
	if (isValidPostcode(query)) {
		search_place(query);
	} else {
		search_name(query);
	}
}

async function search_name(name) {
	if (!name) return;
	name = name.replace(/\+/g, " ");
	search_url = "?search=" + encodeURIComponent(name);

	searchLoader("start");
	if (!isDevMode) {
		sendEvent("event", undefined, "church_search", "name", encodeURIComponent(name), true);
	}

	try {
		const data = await fetchJson("/actions/church-search/search/search-name", { name });
		process_search(data, "name");
	} catch (err) {
		if (err.name !== "AbortError") console.error("search_name failed:", err);
	} finally {
		searchLoader("end");
	}
}

async function search_place(place) {
	if (!place) return;
	search_url = "?search=" + encodeURIComponent(place);

	searchLoader("start");
	if (!isDevMode) {
		sendEvent("event", undefined, "church_search", "place", encodeURIComponent(place), true);
	}

	try {
		const data = await fetchJson("/actions/church-search/search/search-place", { place });
		process_search(data, "place");
	} catch (err) {
		if (err.name !== "AbortError") console.error("search_place failed:", err);
	} finally {
		searchLoader("end");
	}
}

async function search_lat_lon(lat, lon) {
	if (!lat || !lon) return;
	search_url = "?search_lat=" + encodeURIComponent(lat) + "&search_lon=" + encodeURIComponent(lon);

	searchLoader("start");
	if (!isDevMode) {
		sendEvent("event", undefined, "church_search", "latlon", encodeURIComponent(lat + "," + lon), true);
	}

	try {
		const data = await fetchJson("/actions/church-search/search/search-location", { lat, lon });
		process_search(data, "place");
	} catch (err) {
		if (err.name !== "AbortError") console.error("search_lat_lon failed:", err);
	} finally {
		searchLoader("end");
	}
}

async function search_all() {
	if (sidebar) sidebar.classList.add("hidden");
	if (searchInput) searchInput.value = "";
	search_url = "";

	searchLoader("start");

	try {
		const data = await fetchJson("/actions/church-search/search/search-all");
		process_search(data, "all");
	} catch (err) {
		if (err.name !== "AbortError") console.error("search_all failed:", err);
	} finally {
		searchLoader("end");
	}
}

async function loadAllMarkers() {
	searchLoader("start");
	try {
		const data = await fetchJson("/actions/church-search/search/search-all");
		if (!data || data.length === 0) return;
		const newmarkers = data.map((val) => ({
			lat: val.actual_latitude || val.latitude,
			lng: val.actual_longitude || val.longitude,
			name: val.name,
			slug: val.slug,
		}));
		newlistings = data;
		updateMarkers(newmarkers);
		if (mapContainer) mapContainer.classList.remove("hidden");
	} catch (err) {
		if (err.name !== "AbortError") console.error("loadAllMarkers failed:", err);
	} finally {
		searchLoader("end");
	}
}

// --- Process results ---

function process_search(data, type) {
	if (!data || !type) return;

	updateUrl();

	if (data.length === 0) {
		if (noResults) noResults.classList.remove("hidden");
		if (currentView === "list") {
			// No matches — clear the list and let the user adjust their search
			showListLoading(false);
			if (resultsContainer) resultsContainer.innerHTML = "";
			if (sidebar) sidebar.classList.add("hidden");
			teardownObserver();
			updateListCount(0);
		} else {
			if (sidebar) sidebar.classList.add("hidden");
			loadAllMarkers();
		}
		return;
	}

	if (mapContainer) mapContainer.classList.remove("hidden");
	if (noResults) noResults.classList.add("hidden");

	const newmarkers = [];
	newlistings = [];

	for (let i = 0; i < data.length; i++) {
		const val = data[i];
		const latitude = val.actual_latitude || val.latitude;
		const longitude = val.actual_longitude || val.longitude;

		newmarkers.push({
			lat: latitude,
			lng: longitude,
			name: val.name,
			slug: val.slug,
		});

		if (type !== "all") {
			val.address =
				(val.address1 ? val.address1 + ", " : "") +
				(val.address2 ? val.address2 + ", " : "") +
				(val.address3 ? val.address3 + ", " : "") +
				(val.town ? val.town + ", " : "") +
				(val.county ? val.county + ", " : "") +
				(val.postcode || "");
		}

		newlistings.push(val);
	}

	// Track the dataset backing the list view
	currentListings = newlistings;
	currentListType = type === "all" ? "all" : "search";
	if (type === "all") allChurchesData = newlistings;

	// A new dataset resets the A–Z selection
	resetLetter();

	if (currentView === "list") {
		renderList();
	} else if (type !== "all") {
		updateSidebar(newlistings);
	}

	updateMarkers(newmarkers);

	if (currentView === "map" && type !== "all") {
		scrollToMap();
	}
}

// --- InfoWindow content builder ---

function buildInfoContent(church) {
	if (!church) return "";

	if (typeof church.address === "undefined") {
		church.address =
			(church.address1 ? church.address1 + ", " : "") +
			(church.address2 ? church.address2 + ", " : "") +
			(church.address3 ? church.address3 + ", " : "") +
			(church.town ? church.town + ", " : "") +
			(church.county ? church.county + ", " : "") +
			(church.postcode || "");
	}

	let html =
		'<div id="content" class="font-sans-primary overflow-hidden mb-4 mr-2">' +
		'<h1 class="font-bold font-sans-secondary text-primary-400 border-b-3 text-lg sm:text-2xl">' +
		church.name +
		"</h1>" +
		'<div class="text-sm"><p>';

	if (church.address) {
		html += '<i class="far fa-map-marked-alt text-primary-400 mr-2 mt-2"></i> ' + church.address + "<br>";
	}
	if (church.membershipID) {
		html += '<i class="far fa-id-card text-primary-400 mr-2 mt-2"></i> ' + church.membershipID + "<br>";
	}
	if (church.website) {
		const link = isValidHttpUrl(church.website) ? church.website : "http://" + church.website;
		html +=
			'<i class="fas fa-globe text-primary-400 mr-2 mt-2"></i><a href="' +
			link +
			'" target="_blank" rel="noopener noreferrer">' +
			church.website +
			"</a><br>";
	}
	if (church.phone) {
		html += '<i class="far fa-phone text-primary-400 mr-2 mt-2"></i>' + church.phone + "<br>";
	}
	if (church.denomination) {
		html += '<i class="far fa-church text-primary-400 mr-2 mt-2"></i>' + church.denomination;
	}

	html +=
		"</p>" +
		'<p class="my-2 text-right w-full font-bold"><a href="/churches/' +
		church.slug +
		'">Go to church profile &rarr;</a></p>' +
		"</div></div>";

	return html;
}

// --- Sidebar ---

// Build the HTML for a single church result item (shared by the map-view
// sidebar and the list view), filling the .sidebar-template placeholders.
function buildChurchItem(val, i) {
	if (!val || !sidebarTemplate) return "";
	let html = sidebarTemplate.innerHTML;

	// Ensure the display address is built (search-all results skip this earlier)
	if (typeof val.address === "undefined" || val.address === "") {
		val.address =
			(val.address1 ? val.address1 + ", " : "") +
			(val.address2 ? val.address2 + ", " : "") +
			(val.address3 ? val.address3 + ", " : "") +
			(val.town ? val.town + ", " : "") +
			(val.county ? val.county + ", " : "") +
			(val.postcode || "");
	}

	html = html.replace("[marker_id]", i);
	html = html.replace("[church_uid]", val.slug);
	html = html.replace("[number]", i + 1);
	html = html.replace("[name]", val.name);
	html = html.replace("[address]", val.address);

	if (val.distance) {
		const dist = parseFloat(val.distance).toFixed(2);
		html = html.replace(
			"[distance]",
			'<p class="flex items-start"><i class="far fa-route text-primary-200 mr-2 mt-0.5 flex-shrink-0"></i><span>' +
				dist +
				" miles</span></p>"
		);
	} else {
		html = html.replace("[distance]", "");
	}

	if (val.membershipID) {
		html = html.replace(
			"[extra_mem_num]",
			'<p class="flex items-start"><i class="far fa-id-card text-primary-200 mr-2 mt-0.5 flex-shrink-0"></i><span>Membership: ' +
				val.membershipID +
				"</span></p>"
		);
	} else {
		html = html.replace("[extra_mem_num]", "");
	}

	html = html.replace(
		"[go_to_profile]",
		'<p class="mt-2"><a class="font-bold text-primary-200 hover:text-primary-100 transition" href="/churches/' +
			val.slug +
			'">Go to church profile &rarr;</a></p>'
	);

	if (val.denomination) {
		html = html.replace(
			"[extra_denomination]",
			'<p class="flex items-start"><i class="far fa-church text-primary-200 mr-2 mt-0.5 flex-shrink-0"></i><span>' +
				val.denomination +
				"</span></p>"
		);
	} else {
		html = html.replace("[extra_denomination]", "");
	}

	if (val.website) {
		const http_link = isValidHttpUrl(val.website) ? val.website : "http://" + val.website;
		html = html.replace(
			"[extra_website]",
			'<p class="flex items-start"><i class="fas fa-globe text-primary-200 mr-2 mt-0.5 flex-shrink-0"></i><a href="' +
				http_link +
				'" target="_blank" rel="noopener noreferrer" class="text-primary-200 hover:underline truncate">' +
				val.website +
				"</a></p>"
		);
	} else {
		html = html.replace("[extra_website]", "");
	}

	if (val.phone) {
		html = html.replace(
			"[extra_tel]",
			'<p class="flex items-start"><i class="far fa-phone text-primary-200 mr-2 mt-0.5 flex-shrink-0"></i><span>' +
				val.phone +
				"</span></p>"
		);
	} else {
		html = html.replace("[extra_tel]", "");
	}

	return html;
}

function updateSidebar(listings) {
	if (!listings || !resultsContainer || !sidebarTemplate) return;

	sidebar.classList.remove("hidden");
	const fragment = document.createDocumentFragment();
	const tempDiv = document.createElement("div");

	for (let i = 0; i < listings.length; i++) {
		tempDiv.innerHTML = buildChurchItem(listings[i], i);
		while (tempDiv.firstChild) {
			fragment.appendChild(tempDiv.firstChild);
		}
	}

	resultsContainer.innerHTML = "";
	resultsContainer.appendChild(fragment);
}

// --- List view ---

function setView(view) {
	if (view !== "list" && view !== "map") view = "map";
	currentView = view;

	if (finderWrapper) finderWrapper.setAttribute("data-view", view);
	try {
		window.localStorage.setItem("eaukChurchView", view);
	} catch (e) {}

	viewToggleBtns.forEach((btn) => {
		btn.setAttribute("aria-pressed", btn.dataset.view === view ? "true" : "false");
	});

	updateUrl();

	if (view === "list") {
		enterListView();
	} else {
		enterMapView();
	}
}

function buildUrl() {
	let url = "/churches" + (search_url || "");
	if (currentView === "list") {
		url += (search_url ? "&" : "?") + "view=list";
	}
	return url;
}

function updateUrl() {
	window.history.replaceState({}, document.title, buildUrl());
}

function enterListView() {
	if (currentListings && currentListings.length) {
		renderList();
	} else if (allChurchesData && allChurchesData.length) {
		currentListings = allChurchesData;
		currentListType = "all";
		renderList();
	} else {
		// Nothing loaded yet — fetch every member church, then render
		showListLoading(true);
		search_all();
	}
}

function enterMapView() {
	teardownObserver();

	// Reconcile the .results list with map-view semantics
	if (currentListType === "all") {
		if (sidebar) sidebar.classList.add("hidden");
		if (resultsContainer) resultsContainer.innerHTML = "";
	} else if (currentListings && currentListings.length) {
		updateSidebar(currentListings);
	}

	// The map may have initialised (or gained markers) while hidden — refresh it
	if (map) {
		google.maps.event.trigger(map, "resize");
		if (global_markers.length > 1) {
			const bounds = global_markers.reduce(
				(b, m) => b.extend(m.getPosition()),
				new google.maps.LatLngBounds()
			);
			map.fitBounds(bounds);
		} else if (global_markers.length === 1) {
			map.setCenter(global_markers[0].getPosition());
		} else if (overviewCenter) {
			map.setCenter(overviewCenter);
			if (overviewZoom) map.setZoom(overviewZoom);
		}
	}
}

function showListLoading(show) {
	if (listLoading) listLoading.classList.toggle("hidden", !show);
}

function updateListCount(n) {
	if (!listCount) return;
	if (n === 0) {
		listCount.textContent = "";
	} else if (currentListType === "all") {
		listCount.textContent = n === 1 ? "1 church" : n.toLocaleString() + " churches";
	} else {
		listCount.textContent = n === 1 ? "1 result" : n + " results";
	}
}

// First letter of a name, normalised to A–Z (anything else groups under "#")
function firstLetter(name) {
	const ch = (name || "").trim().charAt(0).toUpperCase();
	return ch >= "A" && ch <= "Z" ? ch : "#";
}

// Disable A–Z buttons that have no churches in the given dataset
function updateAzAvailability(list) {
	if (!azButtons || !azButtons.length) return;

	const present = new Set();
	for (let i = 0; i < list.length; i++) {
		present.add(firstLetter(list[i].name));
	}

	azButtons.forEach((btn) => {
		const letter = btn.dataset.letter;
		if (letter === "all") return; // always available
		const has = present.has(letter);
		btn.disabled = !has;
		btn.classList.toggle("opacity-40", !has);
		btn.classList.toggle("cursor-not-allowed", !has);
	});
}

function setLetter(letter) {
	currentLetter = letter;
	if (azButtons) {
		azButtons.forEach((btn) => {
			btn.setAttribute("aria-pressed", btn.dataset.letter === letter ? "true" : "false");
		});
	}
	renderList();
}

function resetLetter() {
	currentLetter = "all";
	if (azButtons) {
		azButtons.forEach((btn) => {
			btn.setAttribute("aria-pressed", btn.dataset.letter === "all" ? "true" : "false");
		});
	}
}

function renderList() {
	if (!resultsContainer) return;

	showListLoading(false);
	if (noResults) noResults.classList.add("hidden");
	teardownObserver();

	const base = currentListings || [];

	// Enable only the A–Z letters that have churches in the current dataset
	updateAzAvailability(base);

	// Filter by the selected first letter
	let items = base;
	if (currentLetter !== "all") {
		items = base.filter((c) => firstLetter(c.name) === currentLetter);
	}

	// Alphabetical for the full directory; keep backend order for search results
	if (currentListType === "all") {
		items = items.slice().sort((a, b) => (a.name || "").localeCompare(b.name || ""));
	}

	filteredListings = items;
	listPage = 0;
	resultsContainer.innerHTML = "";

	updateListCount(items.length);
	if (listEmpty) listEmpty.classList.toggle("hidden", items.length !== 0);
	if (sidebar) sidebar.classList.toggle("hidden", items.length === 0);

	if (items.length) renderNextPage();
}

function renderNextPage() {
	if (!resultsContainer) return;

	const start = listPage * LIST_PAGE_SIZE;
	const end = Math.min(start + LIST_PAGE_SIZE, filteredListings.length);
	if (start >= end) {
		teardownObserver();
		return;
	}

	const fragment = document.createDocumentFragment();
	const tempDiv = document.createElement("div");
	for (let i = start; i < end; i++) {
		tempDiv.innerHTML = buildChurchItem(filteredListings[i], i);
		while (tempDiv.firstChild) {
			fragment.appendChild(tempDiv.firstChild);
		}
	}

	// Insert before the sentinel so it always stays at the end of the list
	if (listSentinel && listSentinel.parentNode === resultsContainer) {
		resultsContainer.insertBefore(fragment, listSentinel);
	} else {
		resultsContainer.appendChild(fragment);
	}
	listPage++;

	if (end < filteredListings.length) {
		ensureObserver();
	} else {
		teardownObserver();
	}
}

function ensureObserver() {
	if (!resultsContainer) return;

	if (!listSentinel) {
		listSentinel = document.createElement("li");
		listSentinel.className = "list-sentinel";
		listSentinel.setAttribute("aria-hidden", "true");
	}
	if (listSentinel.parentNode !== resultsContainer) {
		resultsContainer.appendChild(listSentinel);
	}

	if (!listObserver) {
		listObserver = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) renderNextPage();
				});
			},
			{ rootMargin: "400px" }
		);
	}
	listObserver.observe(listSentinel);
}

function teardownObserver() {
	if (listObserver && listSentinel) listObserver.unobserve(listSentinel);
	if (listSentinel && listSentinel.parentNode) {
		listSentinel.parentNode.removeChild(listSentinel);
	}
}

function toggleChurchDetails(church) {
	const extra = qs(".extra-details", church);
	const more = qs(".more-details", church);

	if (church.classList.contains("expanded")) {
		church.classList.remove("expanded", "bg-gray-50");
		if (extra) extra.classList.add("hidden");
		if (more) more.classList.remove("rotate-180");
	} else {
		church.classList.add("expanded", "bg-gray-50");
		if (extra) extra.classList.remove("hidden");
		if (more) more.classList.add("rotate-180");
	}
}

// --- Marker helpers ---

function highlightMarker(marker) {
	if (marker && marker.marker) {
		marker = marker.marker;
	}

	infowindow.close();

	if (activeMarker) {
		activeMarker.setIcon(search_icon);
		activeMarker.setZIndex(1);
	}

	marker.setIcon(search_icon_hover);
	marker.setZIndex(50);
	activeMarker = marker;

	map.setZoom(14);
	map.panTo(marker.position);
}

function restoreOverview() {
	if (activeMarker) {
		activeMarker.setIcon(search_icon);
		activeMarker.setZIndex(1);
		activeMarker = null;
	}
	infowindow.close();
	collapseAllChurches();

	if (overviewCenter && overviewZoom) {
		map.setZoom(overviewZoom);
		map.panTo(overviewCenter);
	}
}

// --- Batched marker creation ---

function createSingleMarker(pos, i) {
	const position = new google.maps.LatLng(pos.lat, pos.lng);
	const church = newlistings[i];

	const marker = new google.maps.Marker({
		position,
		icon: search_icon,
	});

	global_markers_data[i] = {
		position,
		slug: pos.slug,
		marker,
	};

	marker.addListener("click", () => {
		expandChurch(pos.slug, marker, true);
	});

	global_markers.push(marker);
}

function setMarkers(markers, onComplete) {
	global_markers.length = 0;
	global_markers_data.length = 0;

	const BATCH_SIZE = 250;
	let idx = 0;

	function processBatch() {
		const end = Math.min(idx + BATCH_SIZE, markers.length);
		for (let i = idx; i < end; i++) {
			createSingleMarker(markers[i], i);
		}
		idx = end;

		if (idx < markers.length) {
			requestAnimationFrame(processBatch);
		} else if (onComplete) {
			onComplete();
		}
	}

	processBatch();
}

function collapseAllChurches() {
	qsa(".results .church").forEach((el) => {
		el.classList.remove("selected", "expanded", "bg-gray-50");
		const extra = qs(".extra-details", el);
		if (extra) extra.classList.add("hidden");
		const more = qs(".more-details", el);
		if (more) more.classList.remove("rotate-180");
	});
}

function expandChurch(uid, marker, from_marker) {
	highlightMarker(marker);

	// Open infowindow for this marker
	const markerIndex = global_markers.indexOf(marker);
	if (markerIndex !== -1 && newlistings[markerIndex]) {
		infowindow.setContent(buildInfoContent(newlistings[markerIndex]));
		infowindow.open({ anchor: marker, map });
	}

	// Update sidebar — collapse all, then expand the matching one
	collapseAllChurches();

	const church_container = qs(`.results .church[data-church-uid="${uid}"]`);
	if (church_container) {
		church_container.classList.add("selected", "expanded", "bg-gray-50");

		const extra = qs(".extra-details", church_container);
		if (extra) extra.classList.remove("hidden");

		const more = qs(".more-details", church_container);
		if (more) more.classList.add("rotate-180");

		if (from_marker) {
			church_container.scrollIntoView({ behavior: "smooth", block: "nearest" });
		}
	}

	scrollToMap();
}

function updateMarkers(markers) {
	removeMarkers();

	setMarkers(markers, function onAllMarkersReady() {
		markerCluster = new markerClusterer.MarkerClusterer({
			markers: global_markers,
			map: map,
			renderer: CLUSTER_RENDERER,
		});

		const bounds = global_markers.reduce((b, m) => b.extend(m.getPosition()), new google.maps.LatLngBounds());

		if (markers.length > 1) {
			map.fitBounds(bounds);
		} else {
			map.setCenter(bounds.getCenter());
		}

		google.maps.event.addListenerOnce(map, "idle", () => {
			overviewCenter = map.getCenter();
			overviewZoom = map.getZoom();

			if (markers.length === 1) {
				expandChurch(markers[0].slug, global_markers[0], false);
			}
		});
	});
}

function removeMarkers() {
	if (markerCluster) {
		markerCluster.clearMarkers();
		markerCluster = null;
	}
	global_markers.length = 0;
	global_markers_data.length = 0;
	activeMarker = null;
	if (infowindow) infowindow.close();
}

function scrollToMap() {
	scrollToEl(finderWrapper);
}

// --- Reverse geocode ---

function reverseGeocode(lat, lon) {
	const geocoder = new google.maps.Geocoder();
	geocoder.geocode({ location: { lat, lng: lon } }, (results, status) => {
		if (status === "OK" && results[0] && searchInput) {
			searchInput.value = results[0].formatted_address;
		}
	});
}